import { LEAD_STATUSES_MAP } from "../../../../Lead/constants/lead-statuses.ts"
import { PROJECT_STATUSES_OBJ } from "../../../../Projects/constants/project-statuses.ts"
import type {
  StatusAutomationMode,
  StatusAutomationRuleDefinition,
  StatusAutomationRuleSetting,
  StatusAutomationSaveErrors,
  StatusAutomationSettings,
  StatusAutomationSettingsPatch
} from "../../../api/status-automation-settings.services.ts"

export const WON_TO_PROJECT_RULE = "lead.won_to_project"

const MODE_ORDER: StatusAutomationMode[] = ["off", "suggest", "auto"]

export interface StatusAutomationDraft {
  enabled: boolean
  notify_owner: boolean
  rules: Record<string, StatusAutomationRuleSetting>
}

export const isAvailable = (definition: StatusAutomationRuleDefinition) =>
  definition.available !== false

export const modesFor = (
  definition: StatusAutomationRuleDefinition
): StatusAutomationMode[] =>
  MODE_ORDER.filter((mode) => definition.allowed_modes.includes(mode))

// A rule whose trigger does not exist yet can only be Off, whatever its
// default or the stored mode says.
const recommendedRule = (
  definition: StatusAutomationRuleDefinition
): StatusAutomationRuleSetting => ({
  mode: isAvailable(definition) ? definition.default_mode : "off",
  params: { ...definition.default_params }
})

// Won → Project is the company's existing auto-create toggle folded in, so the
// stored toggle decides its mode rather than whatever the rules map carries.
export const draftFrom = (
  settings: StatusAutomationSettings
): StatusAutomationDraft => ({
  enabled: settings.enabled,
  notify_owner: settings.notify_owner,
  rules: Object.fromEntries(
    settings.rule_catalog.map((definition) => {
      const stored = settings.rules?.[definition.key]
      const mode = !isAvailable(definition)
        ? "off"
        : definition.key === WON_TO_PROJECT_RULE
          ? settings.auto_create_project_from_lead
            ? "auto"
            : "off"
          : stored?.mode ?? definition.default_mode
      return [
        definition.key,
        {
          mode,
          params: { ...definition.default_params, ...(stored?.params ?? {}) }
        }
      ]
    })
  )
})

// Won → Project keeps the company's current choice: recommending a value for
// it would start (or stop) creating projects nobody asked to change.
export const withRecommendedRules = (
  draft: StatusAutomationDraft,
  catalog: StatusAutomationRuleDefinition[]
): StatusAutomationDraft => ({
  ...draft,
  rules: Object.fromEntries(
    catalog.map((definition) => [
      definition.key,
      definition.key === WON_TO_PROJECT_RULE
        ? draft.rules[definition.key]
        : recommendedRule(definition)
    ])
  )
})

const sameJson = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b)

// The whole rules map goes out when any rule changed, so the save is correct
// whether the server merges `rules` key by key or replaces it.
export const buildPatch = (
  draft: StatusAutomationDraft,
  saved: StatusAutomationDraft
): StatusAutomationSettingsPatch => {
  const patch: StatusAutomationSettingsPatch = {}
  if (draft.enabled !== saved.enabled) patch.enabled = draft.enabled
  if (draft.notify_owner !== saved.notify_owner)
    patch.notify_owner = draft.notify_owner
  if (!sameJson(draft.rules, saved.rules)) {
    patch.rules = draft.rules
    const wonToProject = draft.rules[WON_TO_PROJECT_RULE]
    if (
      wonToProject &&
      wonToProject.mode !== saved.rules[WON_TO_PROJECT_RULE]?.mode
    )
      patch.auto_create_project_from_lead = wonToProject.mode === "auto"
  }
  return patch
}

export const isDirty = (
  draft: StatusAutomationDraft | null,
  saved: StatusAutomationDraft | null
) => !!draft && !!saved && Object.keys(buildPatch(draft, saved)).length > 0

export const ruleCopyKey = (ruleKey: string) =>
  `settingsConfiguration.statusAutomation.rules.${ruleKey.replace(/\./g, "_")}`

// The target is the existing integer status of the rule's own entity, read
// from the rule key so a preview move needs no catalog lookup.
export const targetStatusLabel = (
  ruleKey: string,
  target: number | null | undefined
): string | null => {
  if (target === null || target === undefined) return null
  const label = ruleKey.startsWith("project.")
    ? PROJECT_STATUSES_OBJ[target]?.label
    : LEAD_STATUSES_MAP[target as keyof typeof LEAD_STATUSES_MAP]?.label
  return label ?? null
}

// `{"rules": {"lead.stale.days": ["…"]}}` → `{"lead.stale": ["…"]}`, read
// whether or not the refusal arrives inside the response envelope.
export const ruleErrorsFrom = (data: unknown): Record<string, string[]> => {
  const body = data as
    | (StatusAutomationSaveErrors & { details?: StatusAutomationSaveErrors })
    | null
    | undefined
  const rules = body?.rules ?? body?.details?.rules
  if (!rules || typeof rules !== "object") return {}
  const errors: Record<string, string[]> = {}
  for (const [field, messages] of Object.entries(rules)) {
    const ruleKey = field.split(".").slice(0, 2).join(".")
    const list = Array.isArray(messages) ? messages : [String(messages)]
    errors[ruleKey] = [...(errors[ruleKey] ?? []), ...list.map(String)]
  }
  return errors
}
